<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatCount, formatMoney } from '../utils/format'
import type {
  PayrollAccrualReconciliationRow,
  PayrollAccrualReconciliationSummary,
  PayrollPeriod,
  PayrollPostingReconciliationRow,
  PayrollPostingReconciliationSummary,
  PayrollSummaryReport,
} from '../types/finance'

/**
 * `/payroll/reports` — the read-only payroll reports that share filters:
 * summary totals plus the accrual and posting reconciliations. The employee
 * statement has its own page because it requires one employee.
 */

const { t } = useI18n()
const {
  listPayrollPeriods,
  payrollSummary,
  payrollAccrualReconciliation,
  payrollPostingReconciliation,
} = useFinanceApi()

const periods = ref<PayrollPeriod[]>([])
const summary = ref<PayrollSummaryReport | null>(null)
const accrualRows = ref<PayrollAccrualReconciliationRow[]>([])
const accrualSummary = ref<PayrollAccrualReconciliationSummary | null>(null)
const postingRows = ref<PayrollPostingReconciliationRow[]>([])
const postingSummary = ref<PayrollPostingReconciliationSummary | null>(null)
const loading = ref(true)
const error = ref('')

const filters = ref({
  payroll_period_id: '' as string | number,
  currency_code: '',
  date_from: '',
  date_to: '',
})

const invalidRange = computed(() =>
  Boolean(filters.value.date_from && filters.value.date_to && filters.value.date_from > filters.value.date_to),
)

/** A non-zero reconciliation delta is the whole reason these reports exist. */
const accrualDelta = computed(() => {
  const summaryValues = accrualSummary.value
  if (!summaryValues) return 0
  return Number(summaryValues.gross_amount ?? 0) - Number(summaryValues.payroll_earning_amount ?? 0)
})

const postingDelta = computed(() => Number(postingSummary.value?.posting_delta ?? 0))
const currency = computed(() => summary.value?.filters?.currency_code || filters.value.currency_code || '')

function statusLabel(status: string): string {
  const key = `finance.payrollFlow.accrualStatus${status.charAt(0).toUpperCase()}${status.slice(1)}`
  const label = t(key)
  return label.startsWith('finance.') ? status : label
}

async function load() {
  if (invalidRange.value) {
    error.value = t('finance.common.invalidRange')
    loading.value = false
    return
  }
  loading.value = true
  error.value = ''
  const query = {
    payroll_period_id: filters.value.payroll_period_id,
    currency_code: filters.value.currency_code.trim() || undefined,
    date_from: filters.value.date_from || undefined,
    date_to: filters.value.date_to || undefined,
  }
  try {
    const [summaryResult, accrualResult, postingResult] = await Promise.all([
      payrollSummary(query),
      payrollAccrualReconciliation(query),
      payrollPostingReconciliation(query),
    ])
    summary.value = summaryResult
    accrualRows.value = accrualResult.rows
    accrualSummary.value = accrualResult.summary
    postingRows.value = postingResult.rows
    postingSummary.value = postingResult.summary
  } catch (cause) {
    error.value = formatApiError(cause)
    summary.value = null
    accrualRows.value = []
    accrualSummary.value = null
    postingRows.value = []
    postingSummary.value = null
  } finally {
    loading.value = false
  }
}

async function loadPeriods() {
  try {
    const result = await listPayrollPeriods({ page: 1, per_page: 50 })
    periods.value = result.items
  } catch {
    periods.value = []
  }
}

watch(
  () => [
    filters.value.payroll_period_id,
    filters.value.currency_code,
    filters.value.date_from,
    filters.value.date_to,
  ],
  () => {
    void load()
  },
)

onMounted(() => {
  void loadPeriods()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('finance.payrollFlow.reports.eyebrow') }}</p>
      <h2>{{ t('finance.payrollFlow.reports.title') }}</h2>
      <p>{{ t('finance.payrollFlow.reports.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <select v-model="filters.payroll_period_id" class="filter-select">
      <option value="">{{ t('finance.payrollFlow.allPeriods') }}</option>
      <option v-for="period in periods" :key="period.id" :value="period.id">
        {{ period.code }} · {{ period.name }}
      </option>
    </select>
    <input v-model="filters.currency_code" class="filter-select" type="text" placeholder="UZS">
    <input v-model="filters.date_from" class="date-input" type="date">
    <input v-model="filters.date_to" class="date-input" type="date">
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.refresh') }}
    </button>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else>
    <template v-if="summary">
      <div class="summary-row">
        <article class="summary-card accent">
          <span class="summary-value">{{ formatMoney(summary.runs.net_total, currency) }}</span>
          <span class="summary-label">{{ t('finance.payrollFlow.reports.runNet') }}</span>
        </article>
        <article class="summary-card">
          <span class="summary-value">{{ formatMoney(summary.runs.gross_total, currency) }}</span>
          <span class="summary-label">{{ t('finance.payrollFlow.reports.runGross') }}</span>
        </article>
        <article class="summary-card">
          <span class="summary-value">{{ formatMoney(summary.runs.deduction_total, currency) }}</span>
          <span class="summary-label">{{ t('finance.payrollFlow.reports.runDeductions') }}</span>
        </article>
        <article class="summary-card">
          <span class="summary-value">{{ formatCount(summary.runs.run_count) }}</span>
          <span class="summary-label">{{ t('finance.payrollFlow.reports.runCount') }}</span>
        </article>
        <article class="summary-card">
          <span class="summary-value">{{ formatCount(summary.employees.employee_count) }}</span>
          <span class="summary-label">{{ t('finance.payrollFlow.reports.employeeCount') }}</span>
        </article>
      </div>

      <div class="table-scroll">
        <table class="data-table compact">
          <thead>
            <tr>
              <th>{{ t('finance.payrollFlow.reports.section') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.reports.rows') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.reports.gross') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.reports.settled') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.reports.open') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="cell-name">{{ t('finance.payrollFlow.reports.runs') }}</td>
              <td class="cell-num">{{ formatCount(summary.runs.run_count) }}</td>
              <td class="cell-num">{{ formatMoney(summary.runs.gross_total, currency) }}</td>
              <td class="cell-num">{{ formatMoney(summary.runs.posted_net_total, currency) }}</td>
              <td class="cell-num">
                {{ formatMoney(summary.runs.draft_net_total + summary.runs.approved_net_total, currency) }}
              </td>
            </tr>
            <tr>
              <td class="cell-name">{{ t('finance.payrollFlow.reports.accruals') }}</td>
              <td class="cell-num">{{ formatCount(summary.accruals.accrual_count) }}</td>
              <td class="cell-num">{{ formatMoney(summary.accruals.accrual_gross_total, currency) }}</td>
              <td class="cell-num">{{ formatMoney(summary.accruals.exported_accrual_total, currency) }}</td>
              <td class="cell-num">{{ formatMoney(summary.accruals.approved_accrual_total, currency) }}</td>
            </tr>
            <tr>
              <td class="cell-name">{{ t('finance.payrollFlow.reports.postings') }}</td>
              <td class="cell-num">{{ formatCount(summary.postings.posting_count) }}</td>
              <td class="cell-num">—</td>
              <td class="cell-num">{{ formatMoney(summary.postings.active_posted_total, currency) }}</td>
              <td class="cell-num">{{ formatMoney(summary.postings.reversed_posted_total, currency) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <p v-else class="state-message">{{ t('finance.payrollFlow.reports.empty') }}</p>

    <h3>{{ t('finance.payrollFlow.reports.accrualReconciliation') }}</h3>
    <p class="empty-inline">{{ t('finance.payrollFlow.reports.accrualReconciliationHint') }}</p>

    <p v-if="accrualRows.length" class="action-message" :class="{ 'error-inline': accrualDelta !== 0 }">
      {{ accrualDelta === 0
        ? t('finance.payrollFlow.reports.reconciled')
        : t('finance.payrollFlow.reports.delta', { amount: formatMoney(accrualDelta, currency) }) }}
    </p>

    <p v-if="!accrualRows.length" class="empty-inline">
      {{ t('finance.payrollFlow.reports.noRows') }}
    </p>

    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.common.status') }}</th>
            <th>{{ t('finance.common.currency') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.reports.rows') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.reports.accrualGross') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.reports.payrollEarnings') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.reports.unexportedApproved') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in accrualRows" :key="`${row.status}-${row.currency_code}`">
            <td>
              <span class="status-badge" :class="`status-${row.status}`">{{ statusLabel(row.status) }}</span>
            </td>
            <td>{{ row.currency_code }}</td>
            <td class="cell-num">{{ formatCount(row.accrual_count) }}</td>
            <td class="cell-num">{{ formatMoney(row.gross_amount, row.currency_code) }}</td>
            <td class="cell-num">{{ formatMoney(row.payroll_earning_amount, row.currency_code) }}</td>
            <td class="cell-num">{{ formatMoney(row.unexported_approved_amount, row.currency_code) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3>{{ t('finance.payrollFlow.reports.postingReconciliation') }}</h3>
    <p class="empty-inline">{{ t('finance.payrollFlow.reports.postingReconciliationHint') }}</p>

    <p v-if="postingRows.length" class="action-message" :class="{ 'error-inline': postingDelta !== 0 }">
      {{ postingDelta === 0
        ? t('finance.payrollFlow.reports.reconciled')
        : t('finance.payrollFlow.reports.delta', { amount: formatMoney(postingDelta, currency) }) }}
    </p>

    <p v-if="!postingRows.length" class="empty-inline">
      {{ t('finance.payrollFlow.reports.noRows') }}
    </p>

    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.payrollFlow.runs.code') }}</th>
            <th>{{ t('finance.common.status') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.reports.runNet') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.reports.activePostings') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.reports.reversedPostings') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.reports.deltaColumn') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in postingRows" :key="row.payroll_run_id">
            <td class="cell-code">{{ row.payroll_run_code }}</td>
            <td>{{ row.payroll_run_status }}</td>
            <td class="cell-num">{{ formatMoney(row.run_net_total, row.currency_code) }}</td>
            <td class="cell-num">{{ formatMoney(row.active_posting_total, row.currency_code) }}</td>
            <td class="cell-num">{{ formatMoney(row.reversed_posting_total, row.currency_code) }}</td>
            <td class="cell-num" :class="{ 'is-delta': Number(row.posting_delta) !== 0 }">
              {{ formatMoney(row.posting_delta, row.currency_code) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>
</template>

<style scoped>
.status-provisional {
  border-color: #d9a441;
  color: #7a5510;
  background: #fffaf0;
}

.status-approved,
.status-paid {
  border-color: #4e6651;
  color: #2f4a33;
  background: #eef4f0;
}

.status-exported {
  border-color: #7fa6d9;
  color: #274b7a;
  background: #f0f5fb;
}

.status-rejected,
.status-reversed {
  border-color: #e0b0a6;
  color: #a13c2c;
  background: #fdf3f1;
}

.cell-num.is-delta {
  color: #a13c2c;
  font-weight: 700;
}
</style>
